import React, { useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWorldGamesReport } from '../../../Actions/GamesReportsStatusActions/GamesReportsStatusActions';

const safeNumber = (value, defaultValue = 0) => {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : defaultValue;
};

const formatPercentage = (value) => {
  const num = safeNumber(value);
  return Math.round(num);
};

const generateNormalCurve = (mean = 50, stdDev = 15, points = 100) => {
  const curve = [];
  const minX = 0;
  const maxX = 100;
  const step = (maxX - minX) / points;

  for (let i = 0; i <= points; i++) {
    const x = minX + (i * step);
    const y = (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
              Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2));
    curve.push({ x, y });
  }

  const maxY = Math.max(...curve.map(point => point.y));
  return curve.map(point => ({
    x: point.x,
    y: (point.y / maxY) * 80 // Scale to 80% of chart height
  }));
};

/**
 * DivergentThinkingConstructSection - Specialized component for displaying divergent thinking construct data
 *
 * This component provides a unique UI for the Divergent Thinking construct, featuring:
 * - Left side: Normal distribution (bell curve) with percentile indicator line
 * - Right side: Personalized passages with insights based on the user's percentile position
 *
 * The component fetches its own data using the divergent thinking specific API endpoint
 * (/api/v1/fetch_divergent_report) and shows loading state until data is available.
 * It processes the bar_values response format to extract percentile information.
 *
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID for fetching divergent thinking data
 * @returns {JSX.Element} The divergent thinking dashboard with bell curve and personalized content
 */
const DivergentThinkingConstructSection = ({ userId: propUserId }) => {
  const dispatch = useDispatch();
  const reduxState = useSelector((state) => state.studentPerformenceConstruct);
  const reduxUserId = reduxState?.userId;
  const { reportsStatus: divergentThinkingApiData } = useSelector(state => state?.gamesReportsStatusReducers);
  const isMountedRef = useRef(true);

  // Use prop userId if provided, otherwise fall back to Redux
  const userId = propUserId || reduxUserId;

  // Fetch divergent thinking data when component mounts or userId changes
  useEffect(() => {
    let user_id = userId ? { user_id: userId } : {}
    dispatch(fetchWorldGamesReport(user_id))
  }, [userId, dispatch]);

  // Handle component cleanup during unmounting
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const divergentThinkingData = useMemo(() => {
    // Check if component is still mounted to prevent errors during navigation
    if (!isMountedRef.current) {
      return null;
    }

    // First try to use the divergent thinking specific API data
    if (divergentThinkingApiData && typeof divergentThinkingApiData === 'object') {
      const { bar_values, user_result } = divergentThinkingApiData;

      if (bar_values && Array.isArray(bar_values) && bar_values.length > 0) {
        try {
          // Find the divergent thinking bar value
          const divergentThinkingBar = bar_values.find(item => {
            if (!item || typeof item !== 'object') return false;

            const nodeName = (item.node_name && typeof item.node_name === 'string')
              ? item.node_name.toLowerCase() : '';

            return nodeName.includes('divergent thinking') ||
                   nodeName.includes('divergentthinking') ||
                   nodeName.includes('divergent');
          });

          if (divergentThinkingBar &&
              typeof divergentThinkingBar === 'object' &&
              divergentThinkingBar.value &&
              typeof divergentThinkingBar.value === 'object') {

            // Extract the percentage value based on user_result
            let rawPercentile = null;

            if (user_result && divergentThinkingBar.value[user_result] !== undefined) {
              rawPercentile = divergentThinkingBar.value[user_result];
            } else {
              // Fallback: get the first available value
              const values = Object.values(divergentThinkingBar.value);
              if (values.length > 0) {
                rawPercentile = values[0];
              }
            }

            if (rawPercentile !== undefined && rawPercentile !== null) {
              const percentile = Math.max(0, Math.min(100, safeNumber(rawPercentile)));

              const result = {
                percentile,
                rawData: {
                  ...divergentThinkingBar,
                  user_result,
                  percentage: rawPercentile
                }
              };
              return result;
            }
          }
        } catch (error) {
          // Handle any errors during the find operation
          return null;
        }
      }
    }

    // If we reach here, no valid API data was found
    return null;
  }, [divergentThinkingApiData]);

  const getPassageContent = (percentile) => {
    if (percentile >= 67) {
      return {
        category: 'High Divergent Thinking',
        color: '#22c55e',
        passage: `You excel at generating ideas that span very different categories, a hallmark of flexible, imaginative thinking. When you face an open-ended question, you quickly reach beyond the obvious, pulling in concepts that others seldom connect. This breadth helps you spot novel angles and combine seemingly unrelated elements into fresh solutions.

Because you're comfortable moving between distant mental "neighbourhoods," brainstorming sessions tend to feel energising rather than draining. Peers often notice that your first three ideas already cover more territory than most groups reach after ten. Keep nurturing this range by sampling unfamiliar topics—anything from niche podcasts to museum exhibits—so your already-wide associative map grows even richer.`
      };
    } else if (percentile >= 34) {
      return {
        category: 'Moderate Divergent Thinking',
        color: '#3b82f6',
        passage: `Your idea generation shows a healthy mix of familiar and unexpected elements. Given time, you can branch out into new territory, though you may start with more conventional options before stretching further. This balanced style serves you well when problems call for both practicality and a modest dose of creativity.

To move from steady to standout, try a small "second-thought" habit: once you've listed a few solutions, deliberately push for three additional ideas that feel odd or playful. That gentle nudge often unlocks hidden connections and boosts the variety of your responses. With regular practice, your comfort zone will expand, making it easier to surprise yourself—and others—when fresh thinking is needed.`
      };
    } else {
      return {
        category: 'Developing Divergent Thinking',
        color: '#f59e0b',
        passage: `Right now your ideas tend to cluster around familiar themes, which can make new challenges seem narrower than they truly are. You often generate solid, sensible answers—but many come from the same corner of experience, leaving unexplored paths on the table. The good news is that associative breadth grows quickly with targeted practice.

A helpful first step is to pause after your initial ideas and ask, "What's the least related concept I can think of?" Jot down whatever pops up, even if it seems odd. Repeating this simple stretch trains your mind to roam further afield, building the mental pathways that support creative leaps. Over time you'll notice more diverse solutions arriving faster, turning today's starting point into tomorrow's springboard for innovation.`
      };
    }
  };

  if (!divergentThinkingData) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading divergent thinking data...</p>
        </div>
      </div>
    );
  }

  // Additional safety check to prevent errors during navigation/unmounting
  if (!divergentThinkingData.percentile && divergentThinkingData.percentile !== 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading divergent thinking data...</p>
        </div>
      </div>
    );
  }

  const { percentile } = divergentThinkingData;
  const passageInfo = getPassageContent(percentile);
  const curvePoints = generateNormalCurve();

  const createCurvePath = (points) => {
    if (points.length === 0) return '';

    let path = `M ${points[0].x} ${100 - points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      path += ` L ${points[i].x} ${100 - points[i].y}`;
    }
    return path;
  };

  // Wrap the entire render in a try-catch to handle any unexpected errors during navigation
  try {
    return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col md:flex-row gap-6">
      {/* Left side - Normal Distribution Chart */}
      <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <h3 className="text-lg sm:text-xl md:!text-2xl font-semibold text-black mb-3 sm:!mb-4 text-center">Divergent Thinking Distribution</h3>

        {/* Chart Container */}
        <div className="relative">
          {/* SVG Bell Curve */}
          <div className="relative h-80 border-2 border-gray-400 bg-white">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Bell curve */}
              <path
                d={createCurvePath(curvePoints)}
                fill="rgba(59, 130, 246, 0.2)"
                stroke="rgba(59, 130, 246, 0.8)"
                strokeWidth="0.5"
              />

              {/* Percentile line */}
              <line
                x1={percentile}
                y1="20"
                x2={percentile}
                y2="100"
                stroke="red"
                strokeWidth="0.8"
                strokeDasharray="2,2"
              />
            </svg>

            {/* X-axis labels */}
            <div className="absolute -bottom-1 left-0 w-full flex justify-between text-xs sm:text-sm md:text-lg text-gray-600 transform translate-y-6">
              <span>0</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100</span>
            </div>

            {/* Percentile label positioned above the red line */}
            <div
              className="absolute top-4 transform -translate-x-1/2"
              style={{ left: `${percentile}%` }}
            >
              <div className="text-xs sm:text-sm md:text-lg text-red-600 font-bold bg-white px-1 rounded shadow-sm">
                {formatPercentage(percentile)}%
              </div>
            </div>
          </div>

          {/* X-axis label */}
          <div className="text-center mt-8">
            <div className="text-sm md:text-lg font-medium text-gray-700">
              Divergent Thinking Percentile
            </div>
          </div>
        </div>

        {/* Raw Values Display */}
        <div className="mt-4 sm:mt-6 flex flex-wrap gap-4 w-full">
          <div className="bg-blue-50 p-3 rounded w-full flex-1 text-center">
            <div className="text-sm sm:text-lg font-medium text-gray-600 text-nowrap">Your Divergent Thinking Score</div>
            <div className="text-lg sm:text-2xl font-bold text-blue-600">
              {formatPercentage(percentile)}th percentile
            </div>

          </div>
        </div>

        {/* Percentile Explanation */}
        <div className="mt-4 sm:mt-6 w-full p-3 bg-[#0000000A] rounded-lg border border-gray-200">
          <p className="text-xs sm:text-sm md:text-md text-gray-700 leading-relaxed">
            <strong>About Percentiles:</strong> Percentiles show how your score compares to a nationally representative sample of U.S. adults. For example, if you score in the 75th percentile, you scored higher than 75% of adults in the U.S.
          </p>
        </div>
      </div>

      {/* Right side - Personalized Passage */}
      <div className="w-full md:w-1/2 bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center mb-4">
          <div className="w-5 h-5 rounded mr-3" style={{ backgroundColor: passageInfo.color }}></div>
          <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-black">{passageInfo.category}</h3>
        </div>

        <div className="prose prose-sm max-w-none h-full flex flex-col justify-start">
          <div className="space-y-4 sm:space-y-6">
            {passageInfo.passage.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-xl break-words">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>
      </div>


    </div>
  );
  } catch (error) {
    // Handle any unexpected errors during rendering/navigation
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg border border-red-200">
        <div className="text-center">
          <div className="text-red-600 mb-2">⚠️</div>
          <p className="text-red-700">An error occurred while loading divergent thinking data</p>
          <p className="text-sm text-red-600 mt-1">Please try refreshing the page.</p>
        </div>
      </div>
    );
  }
};

export default DivergentThinkingConstructSection;
